/**
 * Generic Model decorator.
 * This example is about the deprecated Service model.
 *
 * Usage:
 * ```
 *  @Model<IService, IServiceApi>()
 *  class Service
 * ```
 *
 * **Type parameters**
 *
 * Type parameters work in a cascade mode, where each type parameter defaults to the previous one, being the first
 * one the most generic type and the last one the most specific:
 *
 *  * IModel | Plain object model representation. Example: IService.
 *  * IModelApiResponse | Plain object Packlink's API response representation (defaults to IModel). Example: IServiceApiResponse.
 *  * IModelApiCreatePayload | Plain object Packlink's API POST payload representation (defaults to IModelApiResponse). Example: IServiceApiCreatePayload.
 *  * IModelApiUpdatePayload | Plain object Packlink's API PUT payload representation (defaults to IModelApiCreatePayload). Example: IServiceApiUpdatePayload.
 *
 * In cases where there is no representation in the API (or are transformations to a custom model that doesn't fit with the api)
 * we will only need to provide IModel to the Model decorator. Following, we'll illustrate some complex cases:
 *
 * ***Confusing API data model***
 *
 * Let's take as an example /v1/services available_dates
 *
 * ```
 * "available_dates": {
 *     "2019/06/28": "[09:00 , 14:00]",
 *     "2019/06/20": "[09:00 , 14:00]",
 *     "2019/06/21": "[09:00 , 14:00]",
 *     "2019/07/01": "[09:00 , 14:00]",
 *     "2019/06/24": "[09:00 , 14:00]",
 *     "2019/06/25": "[09:00 , 14:00]",
 *     "2019/06/26": "[09:00 , 14:00]",
 *     "2019/06/27": "[09:00 , 14:00]",
 *     "2019/06/19": "[09:00 , 14:00]",
 *     "2019/06/18": "[09:00 , 14:00]",
 *     "2019/07/03": "[09:00 , 14:00]",
 *     "2019/07/02": "[09:00 , 14:00]"
 * }
 * ```
 *
 * Internally, our Service Model transforms this data to another structure:
 *
 * ```
 * "availableDates": [
 *      ...
 *      {
 *          date: moment.Moment,
 *          intervals: [
 *              start: '09:00'
 *              end: '14:00'
 *          ]
 *      }
 * ]
 * ```
 * When we serialize our Service object (to store it in web storage for example), we don't want to undo the transformation done, so we have one
 * interface(IService) to represent our plain object service and another one (IServiceApi) to represent the data returned by Packlink's API
 *
 * **Static methods**
 *
 * Each Model will have the following static methods
 *
 *  * createFromResponse(shipmentData: IServiceApiResponse)?: Service; Method to transform the API model to the SDK model.
 *  * deserialize(shipmentData: IService): Service; Method to transform a serialized object to the SDK model.
 *
 * **Instance methods**
 *
 * Each Model will have the following instance methods:
 *
 *  * clone(): Shipment; Method to clone the SDK model to a new one.
 *  * toJSON(): IService;  Method to transform the SDK model to a serialized object.
 *  * getCreatePayload?(): Method to transform the SDK model to an API create model.
 *  * getUpdatePayload?(): Method to transform the SDK model to an API update model.
 */

function Model() {
  return function (model) {
    return model;
  };
}

export { Model };
//# sourceMappingURL=Model.js.map
